import React from 'react'
import { colors, fontSize, radius, transition } from '../../styles/theme'

interface ProgressBarProps {
  /** 0-100. Omit for an indeterminate bar when the duration is unknown. */
  value?: number
  label?: React.ReactNode
  /** Right-aligned counter beside the label, e.g. "3 of 8". */
  detail?: React.ReactNode
  size?: 'sm' | 'md'
  tone?: 'primary' | 'success' | 'error'
  style?: React.CSSProperties
}

const TONE_COLORS: Record<NonNullable<ProgressBarProps['tone']>, string> = {
  primary: colors.primary,
  success: colors.success,
  error: colors.error,
}

export function ProgressBar({ value, label, detail, size = 'md', tone = 'primary', style }: ProgressBarProps) {
  const height = size === 'sm' ? 4 : 8
  const fill = TONE_COLORS[tone]
  const indeterminate = value === undefined
  const pct = indeterminate ? 0 : Math.max(0, Math.min(100, value))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      {(label || detail) && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
          fontSize: fontSize.xs, color: colors.textSecondary,
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          {detail && <span style={{ flexShrink: 0, color: colors.textMuted }}>{detail}</span>}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuemin={indeterminate ? undefined : 0}
        aria-valuemax={indeterminate ? undefined : 100}
        aria-valuenow={indeterminate ? undefined : Math.round(pct)}
        style={{
          height,
          width: '100%',
          background: colors.border,
          borderRadius: radius.full,
          overflow: 'hidden',
        }}
      >
        <div style={indeterminate
          ? {
              height: '100%',
              width: '35%',
              background: fill,
              borderRadius: 'inherit',
              animation: 'docm-indeterminate 1.1s ease-in-out infinite',
            }
          : {
              height: '100%',
              width: `${pct}%`,
              background: fill,
              borderRadius: 'inherit',
              transition: `width ${transition.base}`,
            }
        } />
      </div>
    </div>
  )
}
